import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar, Users, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/public`;

const BookingEngine = () => {
  const [searchParams] = useSearchParams();
  const propertyId = searchParams.get('property') || '';
  
  const [property, setProperty] = useState(null);
  const [checkIn, setCheckIn] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [checkOut, setCheckOut] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRatePlan, setSelectedRatePlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: search, 2: select, 3: guest info, 4: confirmation
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    if (propertyId) {
      fetchPropertyInfo();
    }
  }, [propertyId]);

  const fetchPropertyInfo = async () => {
    try {
      const response = await axios.get(`${API}/properties/${propertyId}/info`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/search-availability`, {
        property_id: propertyId,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        children
      });
      setAvailableRooms(response.data.available_rooms);
      setStep(2);
    } catch (error) {
      toast.error('Failed to search availability');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRatePlan = (ratePlan) => {
    setSelectedRatePlan(ratePlan);
    setStep(3);
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/create-booking`, {
        property_id: propertyId,
        rate_plan_id: selectedRatePlan.id,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        children,
        guest_name: guestInfo.name,
        guest_email: guestInfo.email,
        guest_phone: guestInfo.phone,
        payment_method_id: 'mock_payment_method'
      });
      setConfirmation(response.data);
      setStep(4);
      toast.success('Booking confirmed!');
    } catch (error) {
      toast.error('Booking failed');
    } finally {
      setLoading(false);
    }
  };

  const nights = Math.ceil((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24));

  if (!propertyId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-slate-600">Invalid property ID</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
            {property?.name || 'Loading...'}
          </h1>
          {property?.address && (
            <p className="text-slate-600 mt-1">{property.address}</p>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Step 1: Search */}
        {step === 1 && (
          <Card className="shadow-xl">
            <CardHeader>
              <CardTitle className="text-2xl">Find Your Perfect Stay</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <Label>Check-in</Label>
                  <Input
                    type="date"
                    value={checkIn}
                    onChange={(e) => setCheckIn(e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Check-out</Label>
                  <Input
                    type="date"
                    value={checkOut}
                    onChange={(e) => setCheckOut(e.target.value)}
                    min={checkIn}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Adults</Label>
                  <Input
                    type="number"
                    min="1"
                    value={adults}
                    onChange={(e) => setAdults(parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Children</Label>
                  <Input
                    type="number"
                    min="0"
                    value={children}
                    onChange={(e) => setChildren(parseInt(e.target.value))}
                    className="mt-1"
                  />
                </div>
              </div>
              <Button
                onClick={handleSearch}
                className="w-full mt-6 bg-gradient-to-r from-cyan-600 to-blue-600"
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Search Available Rooms'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Room */}
        {step === 2 && (
          <div className="space-y-6">
            <Button variant="outline" onClick={() => setStep(1)}>
              ← Back to Search
            </Button>
            
            <div className="grid grid-cols-1 gap-6">
              {availableRooms.map((room, idx) => (
                <Card key={idx} className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-2xl">{room.unit_type.name}</CardTitle>
                    <p className="text-slate-600">
                      Sleeps {room.unit_type.capacity} • {room.available_count} available
                    </p>
                  </CardHeader>
                  <CardContent>
                    {room.rate_plans.map((plan) => (
                      <div
                        key={plan.id}
                        className="flex items-center justify-between p-4 border border-slate-200 rounded-lg mb-3 hover:border-cyan-400 transition-colors"
                      >
                        <div>
                          <p className="font-semibold text-lg">{plan.name}</p>
                          <div className="flex gap-2 mt-2">
                            {plan.is_refundable && (
                              <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                Refundable
                              </span>
                            )}
                            {plan.min_los > 1 && (
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                                Min {plan.min_los} nights
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-3xl font-bold text-cyan-600">
                            ${(plan.base_rate * nights).toFixed(2)}
                          </p>
                          <p className="text-sm text-slate-500">${plan.base_rate}/night × {nights} nights</p>
                          <Button
                            onClick={() => handleSelectRatePlan(plan)}
                            className="mt-2 bg-gradient-to-r from-cyan-600 to-blue-600"
                          >
                            Select
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Guest Info */}
        {step === 3 && selectedRatePlan && (
          <div className="space-y-6">
            <Button variant="outline" onClick={() => setStep(2)}>
              ← Back to Rooms
            </Button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card className="shadow-xl">
                  <CardHeader>
                    <CardTitle>Guest Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleBooking} className="space-y-4">
                      <div>
                        <Label>Full Name *</Label>
                        <Input
                          value={guestInfo.name}
                          onChange={(e) => setGuestInfo({...guestInfo, name: e.target.value})}
                          placeholder="John Doe"
                          required
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={guestInfo.email}
                          onChange={(e) => setGuestInfo({...guestInfo, email: e.target.value})}
                          placeholder="john@example.com"
                          required
                        />
                      </div>
                      <div>
                        <Label>Phone *</Label>
                        <Input
                          type="tel"
                          value={guestInfo.phone}
                          onChange={(e) => setGuestInfo({...guestInfo, phone: e.target.value})}
                          placeholder="+1 234 567 8900"
                          required
                        />
                      </div>
                      <Button
                        type="submit"
                        className="w-full bg-gradient-to-r from-cyan-600 to-blue-600"
                        disabled={loading}
                      >
                        {loading ? 'Confirming...' : 'Confirm Booking'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>

              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle>Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-600">Rate Plan</p>
                    <p className="font-semibold">{selectedRatePlan.name}</p>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Calendar size={16} />
                    <span className="text-sm">{checkIn} → {checkOut}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-600">
                    <Users size={16} />
                    <span className="text-sm">{adults} Adults, {children} Children</span>
                  </div>
                  <div className="border-t pt-4">
                    <div className="flex justify-between mb-2">
                      <span>Base Rate</span>
                      <span>${(selectedRatePlan.base_rate * nights).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span>Tax (10%)</span>
                      <span>${(selectedRatePlan.base_rate * nights * 0.1).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold text-cyan-600 border-t pt-2">
                      <span>Total</span>
                      <span>${(selectedRatePlan.base_rate * nights * 1.1).toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && confirmation && (
          <Card className="shadow-xl max-w-2xl mx-auto">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check size={40} className="text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Booking Confirmed!</h2>
              <p className="text-lg text-slate-600 mb-6">
                Thank you for your reservation at {property?.name}
              </p>
              <div className="bg-slate-50 p-6 rounded-lg mb-6">
                <p className="text-sm text-slate-600 mb-2">Confirmation Number</p>
                <p className="text-3xl font-bold text-cyan-600">{confirmation.confirmation_number}</p>
              </div>
              <p className="text-slate-600 mb-2">
                A confirmation email has been sent to <strong>{guestInfo.email}</strong>
              </p>
              <p className="text-slate-600">
                Total Amount: <strong className="text-cyan-600">${confirmation.total_amount}</strong>
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BookingEngine;
